import * as React from 'react';
import { Link, useParams } from 'react-router-dom';
import { ArrowLeft, Clock, ExternalLink, Globe, Zap } from 'lucide-react';
import { findScraper } from '@proptr/shared';
import type {
  ScraperJobDefinition,
  ScraperRunItem,
  ScraperRunListResponse,
} from '@proptr/shared';
import { AppShell } from '../components/layout/AppShell.js';
import { Button } from '../components/ui/button.js';
import { Card } from '../components/ui/card.js';
import { cn } from '../lib/utils.js';
import { client } from '../main.js';

const PAGE_SIZE = 25;

export function ScraperDetailPage() {
  const { scraperId = '' } = useParams();
  const scraper = findScraper(scraperId);

  const [jobFilter, setJobFilter] = React.useState<string>('');
  const [page, setPage] = React.useState(1);
  const [data, setData] = React.useState<ScraperRunListResponse | null>(null);
  const [error, setError] = React.useState<string | null>(null);

  React.useEffect(() => {
    if (!scraper) return;
    let alive = true;
    setError(null);
    client.admin
      .listScraperRuns(scraper.id, {
        jobId: jobFilter || undefined,
        page,
        limit: PAGE_SIZE,
      })
      .then((res) => {
        if (alive) setData(res);
      })
      .catch((err: Error) => {
        if (alive) setError(err.message);
      });
    return () => {
      alive = false;
    };
  }, [scraper, jobFilter, page]);

  if (!scraper) {
    return (
      <AppShell>
        <Card className="p-6">
          <div className="font-mono text-[12px] text-muted-foreground">
            Unknown scraper. <Link to="/scrapers" className="text-primary">Back to scrapers</Link>
          </div>
        </Card>
      </AppShell>
    );
  }

  const items = data?.items ?? null;
  const meta = data?.meta;

  return (
    <AppShell>
      <div className="space-y-8">
        <div>
          <Link
            to="/scrapers"
            className="mb-3 inline-flex items-center gap-1.5 font-mono text-[10px] uppercase tracking-[0.08em] text-muted-foreground hover:text-foreground"
          >
            <ArrowLeft className="h-3 w-3" />
            Scrapers
          </Link>
          <h1 className="font-display text-[40px] font-medium leading-none tracking-[-0.02em]">
            {scraper.name}
          </h1>
          <div className="mt-3 flex flex-wrap items-center gap-2 font-mono text-[11px] text-muted-foreground">
            <Globe className="h-3 w-3" />
            <a
              href={scraper.sourceUrl}
              target="_blank"
              rel="noreferrer"
              className="inline-flex items-center gap-1 hover:text-foreground"
            >
              {scraper.source}
              <ExternalLink className="h-3 w-3" />
            </a>
            <span className="pill pill-muted">{scraper.region}</span>
          </div>
        </div>

        {/* Per-job rollup */}
        <div className="grid gap-4 md:grid-cols-2">
          {scraper.jobs.map((job) => (
            <JobCard
              key={job.id}
              job={job}
              summary={data?.summary.find((s) => s.jobId === job.id)}
            />
          ))}
        </div>

        {/* Run history */}
        <div className="space-y-3">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div className="eyebrow">Run history</div>
            <div className="flex items-center gap-1">
              <FilterChip
                active={jobFilter === ''}
                onClick={() => {
                  setJobFilter('');
                  setPage(1);
                }}
              >
                All
              </FilterChip>
              {scraper.jobs.map((j) => (
                <FilterChip
                  key={j.id}
                  active={jobFilter === j.id}
                  onClick={() => {
                    setJobFilter(j.id);
                    setPage(1);
                  }}
                >
                  {j.name}
                </FilterChip>
              ))}
            </div>
          </div>

          {error ? (
            <Card className="p-6">
              <div className="font-mono text-[12px] text-destructive">{error}</div>
            </Card>
          ) : items === null ? (
            <Card className="p-6">
              <div className="font-mono text-[12px] text-muted-foreground">Loading…</div>
            </Card>
          ) : items.length === 0 ? (
            <Card variant="ghost" className="p-8 text-center">
              <div className="font-mono text-[12px] text-muted-foreground">
                No runs recorded yet.
              </div>
              <div className="mx-auto mt-2 max-w-md text-[12px] leading-relaxed text-muted-foreground">
                Runs appear here once the scraper writes to{' '}
                <code className="rounded bg-muted px-1 py-0.5">scraperRuns</code>.
              </div>
            </Card>
          ) : (
            <Card className="overflow-hidden">
              <div className="overflow-x-auto">
                <table className="w-full min-w-[880px] text-left">
                  <thead>
                    <tr className="border-b border-[hsl(var(--hairline))]">
                      <Th>Started</Th>
                      <Th>Job</Th>
                      <Th>Agency / Enterprise</Th>
                      <Th>Fetched</Th>
                      <Th>Duration</Th>
                      <Th>Status</Th>
                    </tr>
                  </thead>
                  <tbody>
                    {items.map((run) => (
                      <RunRow key={run._id} run={run} scraperJobs={scraper.jobs} />
                    ))}
                  </tbody>
                </table>
              </div>
            </Card>
          )}

          {meta && meta.pages > 1 && (
            <div className="flex items-center justify-between">
              <div className="font-mono text-[11px] text-muted-foreground">
                Page {meta.page} of {meta.pages} · {meta.total} runs
              </div>
              <div className="flex gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  disabled={page <= 1}
                  onClick={() => setPage((p) => p - 1)}
                >
                  Previous
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  disabled={page >= meta.pages}
                  onClick={() => setPage((p) => p + 1)}
                >
                  Next
                </Button>
              </div>
            </div>
          )}
        </div>
      </div>
    </AppShell>
  );
}

function JobCard({
  job,
  summary,
}: {
  job: ScraperJobDefinition;
  summary?: { total: number; lastRunAt: string | null; lastStatus: string | null; enterprises: number; counts: Record<string, number> };
}) {
  const scheduled = job.cadence === 'scheduled';
  return (
    <Card className="p-5">
      <div className="flex items-start gap-2.5">
        {scheduled ? (
          <Clock className="mt-0.5 h-4 w-4 shrink-0 text-primary" />
        ) : (
          <Zap className="mt-0.5 h-4 w-4 shrink-0 text-muted-foreground" />
        )}
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-2">
            <span className="text-[15px] font-semibold">{job.name}</span>
            <span className={scheduled ? 'pill pill-accent' : 'pill pill-muted'}>
              {scheduled ? 'Runs itself' : 'Once per agency'}
            </span>
          </div>
          <p className="mt-2 text-[12px] leading-relaxed text-muted-foreground">
            {job.description}
          </p>
        </div>
      </div>

      <div className="mt-4 grid grid-cols-2 gap-3 border-t border-[hsl(var(--hairline))] pt-4 sm:grid-cols-4">
        <Stat label="Runs" value={summary?.total ?? 0} />
        {!scheduled && (
          <Stat label="Enterprises" value={summary?.enterprises ?? 0} />
        )}
        {job.metrics.slice(0, scheduled ? 3 : 2).map((m) => (
          <Stat key={m} label={m} value={summary?.counts?.[m] ?? 0} />
        ))}
      </div>

      <div className="mt-3 flex items-center gap-2 font-mono text-[10px] uppercase tracking-[0.08em] text-muted-foreground">
        <span>Last run</span>
        <span className="text-foreground">
          {summary?.lastRunAt ? formatDate(summary.lastRunAt) : 'never'}
        </span>
        {summary?.lastStatus && <StatusPill status={summary.lastStatus} />}
      </div>
    </Card>
  );
}

function RunRow({
  run,
  scraperJobs,
}: {
  run: ScraperRunItem;
  scraperJobs: ScraperJobDefinition[];
}) {
  const [open, setOpen] = React.useState(false);
  const job = scraperJobs.find((j) => j.id === run.jobId);
  const metaEntries = Object.entries(run.meta ?? {});
  const countEntries = Object.entries(run.counts ?? {});
  const expandable = metaEntries.length > 0 || !!run.error;

  return (
    <>
      <tr
        className={cn(
          'border-b border-[hsl(var(--hairline))] last:border-0',
          expandable && 'cursor-pointer hover:bg-muted/50',
        )}
        onClick={expandable ? () => setOpen((o) => !o) : undefined}
      >
        <Td>
          <div className="font-mono text-[11px] tabular-nums">
            {formatDate(run.startedAt)}
          </div>
          <div className="mt-0.5 font-mono text-[10px] uppercase tracking-[0.06em] text-muted-foreground">
            {run.trigger}
          </div>
        </Td>
        <Td>
          <span className="text-[13px] font-medium">{job?.name ?? run.jobId}</span>
        </Td>
        <Td>
          {run.enterprise ? (
            <>
              <div className="text-[13px] font-medium">
                {run.target?.name ?? run.enterpriseName ?? '—'}
              </div>
              <Link
                to={`/agency/${run.enterprise}`}
                onClick={(e) => e.stopPropagation()}
                className="font-mono text-[10px] text-primary hover:underline"
              >
                {run.enterpriseName ?? run.enterprise}
              </Link>
            </>
          ) : (
            <span className="font-mono text-[11px] text-muted-foreground">
              — whole directory —
            </span>
          )}
        </Td>
        <Td>
          <div className="flex flex-wrap gap-x-3 gap-y-1">
            {countEntries.length === 0 ? (
              <span className="font-mono text-[11px] text-muted-foreground">—</span>
            ) : (
              countEntries.map(([k, v]) => (
                <span key={k} className="font-mono text-[11px]">
                  <span className="font-semibold tabular-nums">
                    {v.toLocaleString()}
                  </span>{' '}
                  <span className="text-muted-foreground">{k}</span>
                </span>
              ))
            )}
          </div>
        </Td>
        <Td>
          <span className="font-mono text-[11px] tabular-nums">
            {formatDuration(run.durationMs)}
          </span>
        </Td>
        <Td>
          <StatusPill status={run.status} />
        </Td>
      </tr>

      {open && expandable && (
        <tr className="border-b border-[hsl(var(--hairline))] bg-muted/30">
          <td colSpan={6} className="px-4 py-3">
            {run.error && (
              <div className="mb-3 rounded-md border border-destructive/40 bg-destructive/10 p-2.5 font-mono text-[11px] text-destructive">
                {run.error}
              </div>
            )}
            {metaEntries.length > 0 && (
              <div className="grid gap-x-6 gap-y-1.5 sm:grid-cols-2 lg:grid-cols-3">
                {metaEntries.map(([k, v]) => (
                  <div key={k} className="flex gap-2 font-mono text-[11px]">
                    <span className="text-muted-foreground">{k}</span>
                    <span className="min-w-0 break-all">
                      {typeof v === 'object' ? JSON.stringify(v) : String(v)}
                    </span>
                  </div>
                ))}
              </div>
            )}
            {run.target?.url && (
              <a
                href={run.target.url}
                target="_blank"
                rel="noreferrer"
                className="mt-3 inline-flex items-center gap-1 font-mono text-[11px] text-primary hover:underline"
              >
                {run.target.url}
                <ExternalLink className="h-3 w-3" />
              </a>
            )}
          </td>
        </tr>
      )}
    </>
  );
}

function FilterChip({
  active,
  onClick,
  children,
}: {
  active: boolean;
  onClick: () => void;
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        'rounded-md px-2.5 py-1 font-mono text-[10px] font-semibold uppercase tracking-[0.08em] transition-colors',
        active
          ? 'bg-primary/12 text-primary'
          : 'text-muted-foreground hover:bg-muted hover:text-foreground',
      )}
    >
      {children}
    </button>
  );
}

function Stat({ label, value }: { label: string; value: number }) {
  return (
    <div>
      <div className="numeral text-[20px] leading-none">
        {value.toLocaleString()}
      </div>
      <div className="mt-1 font-mono text-[10px] uppercase tracking-[0.08em] text-muted-foreground">
        {label}
      </div>
    </div>
  );
}

function StatusPill({ status }: { status: string }) {
  const cls =
    status === 'success'
      ? 'pill pill-success'
      : status === 'failed'
        ? 'pill pill-danger'
        : status === 'partial'
          ? 'pill pill-warning'
          : 'pill pill-accent';
  return <span className={cls}>{status}</span>;
}

function Th({ children }: { children: React.ReactNode }) {
  return (
    <th className="px-4 py-2.5 font-mono text-[10px] font-semibold uppercase tracking-[0.08em] text-muted-foreground">
      {children}
    </th>
  );
}

function Td({ children }: { children: React.ReactNode }) {
  return <td className="px-4 py-3 align-top">{children}</td>;
}

function formatDate(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleString(undefined, {
    year: 'numeric',
    month: 'short',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  });
}

function formatDuration(ms: number): string {
  if (!ms) return '—';
  if (ms < 1000) return `${ms}ms`;
  const s = ms / 1000;
  if (s < 60) return `${s.toFixed(1)}s`;
  const m = Math.floor(s / 60);
  return `${m}m ${Math.round(s % 60)}s`;
}
